import { useRef, useState } from "react";
import { Upload, FileText, Loader2, Sparkles, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/contexts/I18nContext";

/**
 * Zona de subida (dropzone) + botón "Extraer con IA".
 * Toda la lógica de red vive en el padre (`UploadInvoiceModal`) para mantener
 * el estado del formulario centralizado.
 */
export default function AiUploadTab({
  file,
  fileId,
  uploading,
  extracting,
  onFiles,
  onRemove,
  onExtract,
}) {
  const { t, lang } = useI18n();
  const fileInput = useRef(null);
  const [drag, setDrag] = useState(false);

  return (
    <div className="space-y-4 mt-4">
      {!fileId ? (
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDrag(true);
          }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDrag(false);
            onFiles(e.dataTransfer.files);
          }}
          onClick={() => fileInput.current?.click()}
          className={`dropzone rounded-lg p-12 text-center cursor-pointer ${drag ? "active" : ""}`}
          data-testid="dropzone"
        >
          <Upload className="mx-auto mb-3 text-[var(--text-muted)]" size={28} strokeWidth={1.5} />
          <p className="text-sm text-[var(--text-secondary)]">{t("dragDrop")}</p>
          <p className="text-xs text-[var(--text-muted)] mt-2">PDF, PNG, JPG · max 15MB</p>
          <input
            ref={fileInput}
            type="file"
            accept="image/*,application/pdf"
            className="hidden"
            onChange={(e) => onFiles(e.target.files)}
            data-testid="file-input"
          />
        </div>
      ) : (
        <div className="border border-[var(--border-color)] rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <FileText className="text-[var(--primary)] shrink-0" size={20} />
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{file?.name}</p>
              <p className="text-xs text-[var(--text-muted)]">
                {(file?.size / 1024).toFixed(1)} KB
              </p>
            </div>
          </div>
          <Button size="sm" variant="ghost" onClick={onRemove} data-testid="remove-file-btn">
            <X size={16} />
          </Button>
        </div>
      )}

      {uploading && (
        <div className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
          <Loader2 className="animate-spin" size={14} /> {lang === "es" ? "Subiendo..." : "Uploading..."}
        </div>
      )}

      {fileId && (
        <Button
          onClick={onExtract}
          disabled={extracting}
          data-testid="extract-ai-btn"
          className="w-full bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white"
        >
          {extracting ? (
            <>
              <Loader2 className="mr-2 animate-spin" size={14} /> {t("extracting")}
            </>
          ) : (
            <>
              <Sparkles className="mr-2" size={14} /> {t("extractWithAI")}
            </>
          )}
        </Button>
      )}
    </div>
  );
}
